import {Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {WatchlistRecords} from "../models/WatchlistRecord";
import {WatchListService} from "../service/WatchListService";
import {ClientEventService} from "../../services/client-events/ClientEventService";
import {ClientEventType} from "../../services/client-events/models/ClientEventType";
import {AccountSecuritiesRecords} from "../models/AccountSecuritiesRecord";
import {
    AdviserWatchListId,
    BuildAdviserModel,
    BuildPortfolioModel,
    BuildUserModels,
    PortfolioWatchlistId,
    WatchlistModel,
    WatchlistModels
} from "../models/WatchlistModel";
import {QuoteStore} from "../../quote/store/QuoteStore";
import {BuildSummaries, WatchlistSummaries, WatchlistSummary} from "../models/WatchlistSummary";
import {CreateWatchlistId, WatchlistId} from "../models/WatchlistId";
import {Pair, PairOf, Transform} from "../../types/GenericTypes";
import {WatchlistViewModel} from "../models/WatchlistViewModel";
import {flatMap, isNotNull} from "../../js/util/UtilFunctions";
import {CodeAndExchange, Securities, securityEquality, securityMatcher} from "app/instruments/types/CodeAndExchange";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {QuoteRequests} from "../../quote/models/QuoteRequest";
import {WatchlistType} from "../models/WatchlistType";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {QuoteViewModels} from "../../quote/models/QuoteViewModel";
import {AccountStore} from "../../account/store/AccountStore";
import {isPortfoliosUpdatedClientEvent} from "../../services/client-events/models/ClientEventRecord";
import {WatchListViewModelFactory} from "../service/WatchListViewModelFactory";
import {buildQuoteRequest} from "../../quote/util/QuoteStoreUtil";
import {SecurityMap, SecurityMapOf} from "../../security/common/map/SecurityMap";
import {NewsHeadlineIndicatorSourceFactory} from "../../news/service/source/NewsHeadlineIndicatorSourceFactory";
import {UserInterfaceConfigurationService} from "../../services/configuration/UserInterfaceConfigurationService";
import {updateConfig, WatchlistSortColumn, WatchlistSortConfig} from "../models/WatchlistSort";
import {isSameIndicator, NewsHeadlineIndicatorModel} from "../../news/models/NewsHeadlineIndicatorModel";

const ConfigKey = "watchlist-sorting";

export class WatchListStore {

    public static id = "watchListStore";

    private readonly userWatchLists$ = new ReplaySubject<WatchlistModels>(1);

    private readonly portfolioWatchlist$: Observable<WatchlistModel | null>;
    private readonly adviserWatchList$: Observable<WatchlistModel | null>;
    private readonly watchLists$: Observable<WatchlistModels>;
    private readonly defaultWatchlist$: Observable<WatchlistModel | null>;
    private readonly newWatchLists$ = new Subject<WatchlistId>();
    private readonly sorting$: Observable<WatchlistSortConfig | null>;
    private readonly sortingChange$ = new Subject<Pair<WatchlistId, WatchlistSortColumn>>();

    /** @ngInject */
    public constructor(private readonly watchListService: WatchListService,
                       private readonly clientEventService: ClientEventService,
                       private readonly quoteStore: QuoteStore,
                       private readonly currentUserService: CurrentUserService,
                       private readonly accountStore: AccountStore,
                       private readonly watchListViewModelFactory: WatchListViewModelFactory,
                       private readonly newsHeadlineIndicatorSourceFactory: NewsHeadlineIndicatorSourceFactory,
                       private readonly userInterfaceConfigurationService: UserInterfaceConfigurationService) {
        this.portfolioWatchlist$ = this.buildPortfolioWatchList();
        this.adviserWatchList$ = this.buildAdviserWatchList();
        this.watchLists$ = this.buildWatchlists();
        this.defaultWatchlist$ = this.buildDefaultWatchlist();
        this.sorting$ = this.buildSorting();

        this.updateUserWatchlistFromEvent();
    }

    private updateUserWatchlistFromEvent() {
        const clientEvents$ = this.clientEventService
                                  .events()
                                  .filter((evt) => evt.type === ClientEventType.WATCHLIST_CHANGED)
                                  .startWith({type: ClientEventType.WATCHLIST_CHANGED});

        clientEvents$.throttleTime(100)
                     .do(() => console.debug("Updating User Watchlists from event"))
                     .switchMap(() => this.retrieveUserWatchlists())
                     .map(BuildUserModels)
                     .subscribe((models) => this.userWatchLists$.next(models));
    }

    private retrieveUserWatchlists(): Observable<WatchlistRecords> {
        return Observable.fromPromise(this.watchListService.list())
                         .catch((err) => Observable.empty<WatchlistRecords>());
    }

    private buildSorting(): Observable<WatchlistSortConfig | null> {
        const initial$ = Observable.of(1)
                                   .flatMap(() => this.loadConfig());
        return initial$.flatMap((source) => this.watchSortingChanges(source))
                       .do((config) => this.userInterfaceConfigurationService.save(ConfigKey, config))
                       .publishReplay(1)
                       .refCount();
    }

    private loadConfig(): Promise<WatchlistSortConfig | null> {
        return this.userInterfaceConfigurationService.load<WatchlistSortConfig>(ConfigKey);
    }

    private watchSortingChanges(initial: WatchlistSortConfig | null): Observable<WatchlistSortConfig | null> {
        return this.sortingChange$
                   .scan((acc, next) => updateConfig(acc, next), initial)
                   .startWith(initial);
    }

    /* Only creates the "My Portfolio" watchlist if the user has personal Account(s) */
    private buildPortfolioWatchList(): Observable<WatchlistModel | null> {
        const events$ = this.portfolioChangedEvents();
        const userAccounts$ = this.accountStore
                                  .userAccounts
                                  .map((accounts) => accounts.length)
                                  .distinctUntilChanged();

        return Observable.combineLatest(events$, userAccounts$)
                         .filter(([_, accounts]) => accounts > 0)
                         .throttleTime(60000)
                         .switchMap(() => this.loadAccountSecurities())
                         .map(BuildPortfolioModel)
                         .publishBehavior(null)
                         .refCount();
    }

    private portfolioChangedEvents(): Observable<boolean> {
        return this.clientEventService
                   .events()
                   .filter(isPortfoliosUpdatedClientEvent)
                   .map(() => true)
                   .startWith(true);
    }

    private loadAccountSecurities(): Observable<AccountSecuritiesRecords> {
        return Observable.fromPromise(this.watchListService.accountSecurities())
                         .catch((err) => Observable.empty<AccountSecuritiesRecords>());
    }

    private buildWatchlists(): Observable<WatchlistModels> {
        return Observable.combineLatest(this.userWatchLists$, this.portfolioWatchlist$, this.adviserWatchList$)
                         .map(([user, portfolio, adviser]) => {
                             return [portfolio].concat(user).concat(adviser).filter(isNotNull);
                         });
    }

    private buildAdviserWatchList(): Observable<WatchlistModel | null> {
        return this.currentUserService
                   .current
                   .filter((u) => u.isAdviser())
                   .switchMap(() => this.retrieveAdviserList())
                   .map(BuildAdviserModel)
                   .publishBehavior(null)
                   .refCount();
    }

    private retrieveAdviserList(): Observable<Securities> {
        const response = this.watchListService.adviserSecurities();
        return Observable.fromPromise(response).catch((err) => Observable.empty<Securities>());
    }

    private buildDefaultWatchlist(): Observable<WatchlistModel | null> {
        return this.watchLists$.map((watchlists) => {
            return watchlists.filter((wl) => wl.type === WatchlistType.SYSTEM)
                             .find((wl) => wl.id === PortfolioWatchlistId || wl.id === AdviserWatchListId)!;
        });
    }

    public get watchlists(): Observable<WatchlistSummaries> {
        return this.watchLists$
                   .map(BuildSummaries)
                   .observeOn(Scheduler.async);
    }

    public includes(id: WatchlistId): Observable<boolean> {
        return this.watchLists$
                   .map(this.findWatchlist(id))
                   .map(isNotNull);
    }

    private findWatchlist(id: WatchlistId): Transform<WatchlistModels, WatchlistModel | undefined> {
        return (models) => models.find((m) => m.id === id);
    }

    public select(id: WatchlistId): Observable<WatchlistViewModel> {
        const watchlist$ = this.watchLists$
                               .map(this.findWatchlist(id))
                               .filter(isNotNull);
        const securities$ = watchlist$.map((w) => w.securities)
                                      .distinctUntilChanged((a, b) => this.securitiesUnchanged(a, b))
                                      .publishReplay(1)
                                      .refCount();
        const quotes$ = securities$.map((securities) => this.buildQuoteRequests(securities))
                                   .switchMap((requests) => this.retrieveQuotes(requests))
                                   .startWith([])
                                   .sampleTime(1000);
        const headlines$ = securities$.switchMap((securities) => this.indicators(securities))
                                      .startWith(SecurityMapOf())
                                      .distinctUntilChanged((a, b) => a.isSameAs(b, isSameIndicator))
                                      .sampleTime(1000);

        const watchlistSort$ = this.sorting$.map((sorting) => (sorting?.[id] ?? null));

        return Observable.combineLatest(watchlist$, quotes$, headlines$, watchlistSort$)
                         .switchMap(([watchlist, quotes, headlines, sorting]) => {
                             return this.watchListViewModelFactory.buildViewModel(watchlist,
                                                                                  quotes,
                                                                                  headlines,
                                                                                  sorting);
                         });
    }

    private indicators(securities: Securities): Observable<SecurityMap<NewsHeadlineIndicatorModel>> {
        return this.newsHeadlineIndicatorSourceFactory.indicators(securities);
    }

    public toggleSorting(id: WatchlistId, column: WatchlistSortColumn) {
        this.sortingChange$.next(PairOf(id, column));
    }

    private securitiesUnchanged(a: ReadonlyArray<CodeAndExchange>, b: ReadonlyArray<CodeAndExchange>): boolean {
        return ArrayUtil.matches(a, b, securityEquality());
    }

    private buildQuoteRequests(securities: ReadonlyArray<CodeAndExchange>): QuoteRequests {
        return securities.map((s) => buildQuoteRequest(s));
    }

    private retrieveQuotes(requests: QuoteRequests): Observable<QuoteViewModels> {
        if (requests.length === 0) {
            return Observable.of([]);
        } else {
            return this.quoteStore.scan(requests);
        }
    }

    public get newWatchlists(): Observable<WatchlistId> {
        return this.newWatchLists$.observeOn(Scheduler.async);
    }

    public get userWatchlists(): Observable<WatchlistModels> {
        return this.userWatchLists$.observeOn(Scheduler.async);
    }

    public get userCodes(): Observable<ReadonlyArray<CodeAndExchange>> {
        return this.userWatchLists$
                   .observeOn(Scheduler.async)
                   .map((watchlists) => this.watchlistCodes(watchlists));
    }

    private watchlistCodes(watchlists: WatchlistModels): ReadonlyArray<CodeAndExchange> {
        const allCodes = watchlists.map((wl) => wl.securities).reduce(flatMap, []);
        return ArrayUtil.distinct(allCodes, securityMatcher);
    }

    public create(name: string) {
        Observable.of(name)
                  .flatMap((n) => this.watchListService.create(n))
                  .catch((err) => Observable.empty<number>())
                  .map((id) => CreateWatchlistId(id, WatchlistType.USER))
                  .do((id) => this.newWatchLists$.next(id))
                  .withLatestFrom(this.userWatchLists$)
                  .map(([id, watchlists]) => this.addNewWatchlist(id, name, watchlists))
                  .subscribe((watchlists) => this.userWatchLists$.next(watchlists));
    }

    private addNewWatchlist(id: WatchlistId, name: string, watchlists: WatchlistModels): WatchlistModels {
        const newWatchlist: WatchlistModel = {
            id,
            name,
            type: WatchlistType.USER,
            securities: []
        };
        return [newWatchlist].concat(watchlists);
    }

    public get defaultWatchlist(): Observable<WatchlistSummary | null> {
        return this.defaultWatchlist$.observeOn(Scheduler.async);
    }

    public addItem(id: WatchlistId, security: CodeAndExchange) {
        this.addItems(id, [security]);
    }

    public addItems(id: WatchlistId, securities: Securities) {
        this.userWatchLists$
            .first()
            .map(this.findWatchlist(id))
            .filter(isNotNull)
            .map((watchlist) => securities.reduce((acc, next) => this.addItemToWatchlist(next, acc), watchlist))
            .do((watchlist) => this.updateWatchlist(watchlist))
            .withLatestFrom(this.userWatchLists$)
            .map(([watchlist, watchlists]) => this.replaceWatchlist(watchlist, watchlists))
            .subscribe((watchlists) => this.userWatchLists$.next(watchlists));
    }

    private addItemToWatchlist(security: CodeAndExchange, watchlist: WatchlistModel): WatchlistModel {
        const allSecurities = watchlist.securities.concat(security);
        const securities = ArrayUtil.distinct(allSecurities, securityEquality());
        return {
            ...watchlist,
            securities
        };
    }

    public removeItem(id: WatchlistId, security: CodeAndExchange) {
        this.userWatchLists$
            .first()
            .map(this.findWatchlist(id))
            .filter(isNotNull)
            .map((watchlist) => this.removeItemFromWatchlist(watchlist, security))
            .do((watchlist) => this.updateWatchlist(watchlist))
            .withLatestFrom(this.userWatchLists$)
            .map(([watchlist, watchlists]) => this.replaceWatchlist(watchlist, watchlists))
            .subscribe((watchlists) => this.userWatchLists$.next(watchlists));
    }

    private removeItemFromWatchlist(watchlist: WatchlistModel, security: CodeAndExchange): WatchlistModel {
        const securities = watchlist.securities.filter((sec) => !securityMatcher(sec, security));
        return {
            ...watchlist,
            securities
        };
    }

    private replaceWatchlist(watchlist: WatchlistModel, watchlists: WatchlistModels): WatchlistModels {
        return ArrayUtil.replaceBy(watchlists, watchlist, (item) => item.id === watchlist.id);
    }

    private updateWatchlist(watchlist: WatchlistModel) {
        Observable.fromPromise(this.watchListService.updateItems(watchlist.id, watchlist.securities))
                  .catch((err) => Observable.empty());
    }

    public delete(id: WatchlistId) {
        this.userWatchLists$
            .first()
            .map((watchlists) => this.removeWatchlist(id, watchlists))
            .do(() => this.watchListService.delete(id))
            .subscribe((watchlists) => this.userWatchLists$.next(watchlists));
    }

    private removeWatchlist(id: WatchlistId, watchlists: WatchlistModels): WatchlistModels {
        return watchlists.filter((wl) => wl.id !== id);
    }
}
